import {
  Alert,
  AlertTypography,
  Box,
  Button,
  Form,
  Icon,
  Link,
  NotificationCard,
  TextField,
  Typography,
} from '@ace-clublabs/shared-component-library';
import { AppFlow } from '@lib/types';
import React, { useState } from 'react';
import { useGlobalState } from '../shared/state';
import { createAccountOnClick, loginOnClick } from '../utils/urlHelper';
import { ErrorType } from '../shared/types';
import { CLUB_PHONE_NUMBERS } from '@ace-clublabs/payment-types';
import CookieHelper from '../utils/cookieHelper';
import { PolicyHelper } from '@ace-clublabs/payments-helper';
interface Props {
  onSubmit: (values: any) => void;
  hasError: boolean;
  clubCode: string;
  appFlow: AppFlow;
  errorType?: ErrorType;
  initialMembershipNumber?: string;
  initialZipCode?: string;
}

const VerifyEligibilityForm: React.FC<Props> = ({
  onSubmit,
  errorType,
  hasError,
  clubCode,
  appFlow,
  initialMembershipNumber = '',
  initialZipCode = '',
}) => {
  const handleLoginClick = () => loginOnClick(clubCode);
  const handleCreateAccountClick = createAccountOnClick;
  const [isMobileApp] = useGlobalState('isMobileApp');
  const [membershipNumber, setMembershipNumber] = useState(initialMembershipNumber);
  const [membershipNumberErrorMessage, setMembershipNumberErrorMessage] = useState('');
  const [zipCodeErrorMessage, setZipCodeErrorMessage] = useState('');
  const [zipCode, setZipCode] = useState(initialZipCode);

  const getClubCode = () => {
    const clubCodeFromZipCookie = CookieHelper.getClubCodeFromZipcodeCookie();
    if (clubCode) return clubCode;
    if (clubCodeFromZipCookie && clubCodeFromZipCookie !== '000') return clubCodeFromZipCookie;
    if (membershipNumber?.length === 16) return PolicyHelper.getClubCodeFromMemberNumber(membershipNumber);
    return '004';
  };

  const clubPhoneNumber = CLUB_PHONE_NUMBERS[getClubCode()];

  const validateMembershipNumber = (value: string) => {
    if (value.length === 0) return 'Membership number is required.';
    else if (new RegExp('^[0-9]+$').test(value) === false) {
      setMembershipNumberErrorMessage('Membership number must only be numbers.');
    } else if (new RegExp('^(?:.{8}|.{16})$').test(value) === false) {
      setMembershipNumberErrorMessage('Membership number must be 8 or 16 characters.');
    } else setMembershipNumberErrorMessage('');
  };

  const handleMembershipNumberBlur = (value: string) => {
    if (value === '') setMembershipNumberErrorMessage('Membership number is required.');
  };

  const validateZipCode = (value: string) => {
    if (value.length === 0) return 'ZIP code is required.';
    else if (new RegExp('^[0-9]+$').test(value) === false) {
      setZipCodeErrorMessage('ZIP code must only be numbers.');
    } else if (new RegExp('^(?:.{5})$').test(value) === false) {
      setZipCodeErrorMessage('ZIP code must be 5 characters.');
    } else setZipCodeErrorMessage('');
  };

  const handleZipCodeBlur = (value: string) => {
    if (value === '') setZipCodeErrorMessage('ZIP code is required.');
  };

  const enableSubmitButton = () => {
    if (!membershipNumber || !zipCode) return false;
    const _membershipNumber = membershipNumber.replaceAll(' ', '');
    return (
      new RegExp('^(?:.{8}|.{16})$').test(_membershipNumber) &&
      new RegExp('^(?=[0-9]*$)(?:.{8}|.{16})$').test(_membershipNumber) &&
      new RegExp('^\\d{5}').test(zipCode)
    );
  };

  let customErrorMessage: string = '';
  let displayDefaultErrorMessage = true;

  if (
    errorType === ErrorType.AUTOPAY_ALREADY_ENROLLED ||
    errorType === ErrorType.BOT_ERROR ||
    (errorType === ErrorType.INELIGIBLE_FOR_AUTOPAY && appFlow === AppFlow.AUTOPAY)
  ) {
    displayDefaultErrorMessage = false;
  } else if (errorType === ErrorType.PAYMENT_IN_PROCESSING) {
    displayDefaultErrorMessage = false;
    customErrorMessage =
      appFlow === AppFlow.AUTOPAY
        ? 'Auto Pay is unavailable because there is already a payment pending.'
        : 'There is already a payment in processing.';
  }

  return (
    <Box id="eligibility-form">
      <Typography variant="h5Medium" component="h1" id="eligibility-form-header">
        Enter your membership number and ZIP code
      </Typography>
      {errorType === ErrorType.AUTOPAY_ALREADY_ENROLLED && (
        <NotificationCard>
          {appFlow === AppFlow.PAYMENT ? (
            <Typography variant="b2Regular">
              You're enrolled in Auto Pay, so your account will be charged automatically.
            </Typography>
          ) : (
            <Typography variant="b2Regular">
              You are already enrolled in Auto Pay.{' '}
              <Link id="login-link" component="a" href="https://app.ace.aaa.com/payments/manage-autopay/">
                Log in
              </Link>{' '}
              to manage your Auto Pay details.
            </Typography>
          )}
        </NotificationCard>
      )}
      {errorType === ErrorType.INELIGIBLE_FOR_AUTOPAY && appFlow === AppFlow.AUTOPAY && (
        <Alert
          id="autopay-ineligible-alert"
          severity="warning"
          className="ineligible-warning-text"
          displayCloseButton={false}
          icon={<Icon iconName="circle-exclamation-regular" />}
          sx={{ mt: 2 }}
        >
          <AlertTypography className="sorry-text">
            Your account is not eligible for Auto Pay. Please call{' '}
            <Link href={`tel:${clubPhoneNumber}`}>{clubPhoneNumber}</Link> for assistance.
          </AlertTypography>
        </Alert>
      )}
      {hasError && displayDefaultErrorMessage && (
        <Alert
          id="unable-accept-payment-alert"
          severity="warning"
          className="ineligible-warning-text"
          displayCloseButton={false}
          icon={<Icon iconName="circle-exclamation-regular" />}
          sx={{ mt: 2 }}
        >
          <AlertTypography className="sorry-text">
            Sorry, we are unable to accept payment on this membership number at this time.
          </AlertTypography>
          <AlertTypography className="call-us-text" sx={{ mt: 2 }}>
            Call us at <Link href={`tel:${clubPhoneNumber}`}>{clubPhoneNumber}</Link> with any questions.
          </AlertTypography>
        </Alert>
      )}
      {customErrorMessage && (
        <Alert
          id="custom-warning"
          severity="warning"
          className="custom-warning"
          displayCloseButton={false}
          sx={{ mt: 2 }}
        >
          <AlertTypography className="sorry-text">{customErrorMessage}</AlertTypography>
        </Alert>
      )}
      <Form onSubmit={onSubmit} id="eligibility-form-inputs">
        <TextField
          type="tel"
          id="membership-number"
          name="membership-number"
          label="Membership number"
          maxLength={16}
          helperText="8- or 16-digit number on membership card or bill."
          onChange={(e: any) => {
            setMembershipNumber(e.target.value);
            validateMembershipNumber(e.target.value);
          }}
          error={membershipNumberErrorMessage.length > 0}
          validationErrorMessage={membershipNumberErrorMessage}
          onBlur={(e: any) => {
            handleMembershipNumberBlur(e.target.value);
          }}
          onClearButtonClick={() => setMembershipNumber('')}
          fullWidth
        />
        <TextField
          type="tel"
          id="zip-code"
          name="zip-code"
          label="ZIP code"
          maxLength={5}
          helperText="5-digit ZIP code associated with the membership."
          onChange={(e: any) => {
            setZipCode(e.target.value);
            validateZipCode(e.target.value);
          }}
          error={zipCodeErrorMessage.length > 0}
          validationErrorMessage={zipCodeErrorMessage}
          onBlur={(e: any) => {
            handleZipCodeBlur(e.target.value);
          }}
          onClearButtonClick={() => setZipCode('')}
        />
        <Button
          id="verify-next"
          onClick={() => onSubmit({ membershipNumber, zipCode })}
          disabled={!enableSubmitButton()}
          label="Next"
          fullWidth
        />
      </Form>
      {!isMobileApp && appFlow === AppFlow.PAYMENT && (
        <Box id="more-ways-to-pay-container">
          <Typography variant="b1Medium" id="more-ways-to-pay-label">
            More ways to pay
          </Typography>
          <Typography id="more-ways-to-pay-login">
            To schedule payments, enroll in auto pay and save your payment account information for future use{' '}
            <Link component="a" className="primary" onClick={handleLoginClick} id="more-ways-to-pay-login-button">
              Log in.
            </Link>
          </Typography>
          <Typography id="more-ways-to-pay-create-account">
            Don't have an account?{' '}
            <Link
              component="a"
              className="primary"
              onClick={handleCreateAccountClick}
              id="more-ways-to-pay-create-account-button"
            >
              Create one now
            </Link>
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default VerifyEligibilityForm;
